import {
  AvailableUnitExtended,
  GroupedFloorPlan,
  Price,
} from '@greystar/lib/src/types/property-types';
import { FilterParams, RoomType } from './FloorPlanFilters-types';
import { calculateTMLP, formatCurrency, getAllFees, getValidAvailableDate } from '@greystar/lib';
import { pdpFiltersEncodeProp } from '../../component-props';

export const pdpFiltersEncodeDefault = {
  min: '',
  max: '',
  minLease: '',
  maxLease: '',
  bedroomSelected: [],
  bathroomSelected: [],
  moveInSelected: [],
};

export const encodeFiltersToUrl = (filters: pdpFiltersEncodeProp) => {
  const parts: string[] = [];

  if (filters.bedroomSelected.length) {
    const floorPlanBedroomFilter = filters.bedroomSelected.map((item) =>
      item === '0' ? 'studio' : item
    );

    parts.push(`bed-${floorPlanBedroomFilter.join('-')}`);
  }
  if (filters.bathroomSelected.length) parts.push(`bath-${filters.bathroomSelected.join('-')}`);
  if (filters.min) parts.push(`min-${filters.min}`);
  if (filters.max) parts.push(`max-${filters.max}`);
  if (filters.minLease || filters.maxLease)
    parts.push(`lease-${filters.minLease || ''}-${filters.maxLease || ''}`);
  if (filters.moveInSelected.length) parts.push(`movein-${filters.moveInSelected.join('-')}`);

  return parts.length ? `filters=${parts.join('_')}` : '';
};

export const decodeFiltersFromUrl = (search: string) => {
  const params = new URLSearchParams(search);
  const filtersString = params.get('filters');
  const result = {
    min: '',
    max: '',
    minLease: '',
    maxLease: '',
    bedroomSelected: [] as string[],
    bathroomSelected: [] as string[],
    moveInSelected: [] as string[],
  };

  if (!filtersString) return result;

  const parts = filtersString.split('_');

  for (const part of parts) {
    if (part.startsWith('bed-')) {
      result.bedroomSelected = part.replace('bed-', '').split('-');
    } else if (part.startsWith('bath-')) {
      result.bathroomSelected = part.replace('bath-', '').split('-');
    } else if (part.startsWith('min-')) {
      result.min = part.replace('min-', '');
    } else if (part.startsWith('max-')) {
      result.max = part.replace('max-', '');
    } else if (part.startsWith('lease-')) {
      const [, minLease, maxLease] = part.split('-');
      result.minLease = minLease ?? '';
      result.maxLease = maxLease ?? '';
    } else if (part.startsWith('movein-')) {
      result.moveInSelected = part.replace('movein-', '').split('-');
    }
  }

  return result;
};

export const getDynamicRoomOptions = (
  groupedFloorPlans: GroupedFloorPlan[] | null,
  countKey: 'bedroomCount' | 'bathroomCount',
  labelBase: string,
  updateRoomOptions: (event: React.ChangeEvent<HTMLInputElement>, type: string) => void,
  includeStudio: boolean = false
) => {
  const counts = new Set<number>();

  groupedFloorPlans?.forEach((group) => {
    const count = group.floorplan[countKey];
    if (typeof count === 'number') {
      counts.add(count);
    }
  });

  return Array.from(counts)
    .sort((a, b) => a - b)
    .map((count) => {
      if (includeStudio && count === 0) {
        return {
          label: RoomType.studio,
          value: RoomType.studio.toLowerCase(),
          id: RoomType.studio.toLowerCase(),
          inputProps: {
            onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
              updateRoomOptions(event, countKey),
          },
        };
      }
      const pluralSuffix = count > 1 ? 's' : '';
      const value = `${count}`;
      return {
        label: `${count} ${labelBase}${pluralSuffix}`,
        value,
        id: `${value}-${labelBase}`,
        inputProps: {
          onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
            updateRoomOptions(event, countKey),
        },
      };
    });
};

export const getUniqueSortedPrices = (groupedFloorPlans: GroupedFloorPlan[] | null): number[] => {
  const prices = new Set<number>();
  if (!groupedFloorPlans) return [];

  for (const group of groupedFloorPlans) {
    for (const unit of group.units) {
      prices.add(unit.minPrice);
      prices.add(unit.maxPrice);
    }
  }

  return Array.from(prices).sort((a, b) => a - b);
};

export const getUniqueSortedLease = (groupedFloorPlans: GroupedFloorPlan[] | null): number[] => {
  if (!groupedFloorPlans) return [];

  const leaseTerms = new Set<number>();

  groupedFloorPlans.forEach((group) => {
    group.units
      .filter((unit) => Array.isArray(unit.prices))
      .forEach((unit) => {
        unit.prices.forEach((price) => {
          const term = Number(price?.leaseTerm || '12');
          if (!isNaN(term)) {
            leaseTerms.add(term);
          }
        });
      });
  });

  return Array.from(leaseTerms).sort((a, b) => a - b);
};

export const formatPrices = (prices: number[], disableBelow?: number, disableAbove?: number) =>
  prices.map((price) => ({
    label: formatCurrency(price, '$'),
    value: `${price}`,
    disabled:
      (disableBelow !== undefined && price < disableBelow) ||
      (disableAbove !== undefined && price > disableAbove),
  }));

export const formatLease = (leaseTerms: number[], disableBelow?: number, disableAbove?: number) =>
  leaseTerms.map((term) => ({
    label: `${term}`,
    value: `${term}`,
    disabled:
      (disableBelow !== undefined && term < disableBelow) ||
      (disableAbove !== undefined && term > disableAbove),
  }));

export const getMoveInMatch = (availableOn: string | undefined, moveInSelected: string[]) => {
  if (!moveInSelected.length || !availableOn) return true;

  const availableDate = new Date(availableOn);
  const today = new Date();
  const availableDateUTC = Date.UTC(
    availableDate.getUTCFullYear(),
    availableDate.getUTCMonth(),
    availableDate.getUTCDate()
  );
  const todayUTC = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());

  const diffTime = availableDateUTC - todayUTC;
  const diffDays = diffTime / (1000 * 3600 * 24);

  return moveInSelected.some((selected) => {
    const days = parseInt(selected, 10);
    return !isNaN(days) && diffDays <= days;
  });
};

export const getLeaseMatch = (unit: AvailableUnitExtended, leaseSelected: string[]): boolean => {
  if (!unit || !leaseSelected.length) return true;

  const maxLease = Math.max(...leaseSelected.map((s) => parseInt(s, 10)).filter((n) => !isNaN(n)));

  if (isNaN(maxLease)) return true;

  return unit.prices.some((price) => {
    const leaseTerm = parseInt(price.leaseTerm, 10);
    return !isNaN(leaseTerm) && leaseTerm <= maxLease;
  });
};

export const parseNumber = (value: string, fallback = NaN): number =>
  isNaN(parseInt(value, 10)) ? fallback : parseInt(value, 10);

export const isPriceLeaseTermMatch = (
  price: Price,
  minLease: number,
  maxLease: number
): boolean => {
  const lease = parseNumber(price.leaseTerm, 12);
  if (isNaN(lease)) return false;
  const meetsMin = isNaN(minLease) || lease >= minLease;
  const meetsMax = isNaN(maxLease) || lease <= maxLease;
  return meetsMin && meetsMax;
};

export const isRoomMatch = (
  bedrooms: string[],
  bathrooms: string[],
  bedroomCount: number,
  bathroomCount: number
): boolean => {
  const bedroomMatch =
    bedrooms.length === 0 || bedrooms.includes(bedroomCount === 0 ? 'studio' : `${bedroomCount}`);
  const bathroomMatch = bathrooms.length === 0 || bathrooms.includes(`${bathroomCount}`);
  return bedroomMatch && bathroomMatch;
};

export const applyFilters = ({
  groupedFloorPlans,
  property,
  min,
  max,
  minLease,
  maxLease,
  bedroomSelected,
  bathroomSelected,
  moveInSelected,
}: FilterParams): GroupedFloorPlan[] => {
  return (
    groupedFloorPlans?.map((group) => {
      const isFlatPricing = group.units.some((unit) => unit.isFlatPricing);
      const roomMatch = isRoomMatch(
        bedroomSelected,
        bathroomSelected,
        group.floorplan.bedroomCount,
        group.floorplan.bathroomCount
      );

      if (isFlatPricing) {
        const units = group.units.filter((unit) => {
          const priceValue = Number(unit.minPrice);
          const floorPlanTmlp = calculateTMLP(
            priceValue ?? 0,
            getAllFees(property.fees ?? []),
            1,
            getValidAvailableDate(unit.availableOn)
          );
          const meetsMinPrice = isNaN(min) || floorPlanTmlp.amount >= min;
          const meetsMaxPrice = isNaN(max) || floorPlanTmlp.amount <= max;
          const meetsMoveIn = getMoveInMatch(unit.availableOn, moveInSelected);
          return roomMatch && meetsMinPrice && meetsMaxPrice && meetsMoveIn;
        });
        return { ...group, units };
      }

      const units = group.units
        .map((unit) => ({
          ...unit,
          prices: unit.prices.filter((price) => {
            const priceValue = Number(price.price);
            const floorPlanTmlp = calculateTMLP(
              priceValue ?? 0,
              getAllFees(property.fees ?? []),
              1,
              getValidAvailableDate(price.leaseStartsOn)
            );
            const meetsLeaseTerms = isPriceLeaseTermMatch(price, minLease, maxLease);
            const meetsMinPrice = isNaN(min) || floorPlanTmlp.amount >= min;
            const meetsMaxPrice = isNaN(max) || floorPlanTmlp.amount <= max;
            const meetsMoveIn = getMoveInMatch(price.leaseStartsOn, moveInSelected);
            return meetsLeaseTerms && meetsMinPrice && meetsMaxPrice && meetsMoveIn && roomMatch;
          }),
        }))
        .filter((unit) => unit.prices.length > 0);

      return { ...group, units };
    }) ?? []
  );
};
